if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mobileMql = window.matchMedia('(max-width: 749px)');

        if (!this.elements.viewer) return;

        // Keep Dawn's slider component alive on mobile so the counter updates,
        // but never bind any touchmove/preventDefault logic. The gallery stays
        // a native x-axis swipe rail and the PDP keeps normal vertical scroll.
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 120));

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');
            if (!button) return;
            button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }

        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      onSlideChanged(event) {
        const current = event && event.detail && event.detail.currentElement;
        if (!current || !current.dataset || !current.dataset.mediaId) return;

        this.setActiveMediaState(current.dataset.mediaId);

        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${current.dataset.mediaId}"]`);
        if (!thumbnail) return;
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMediaState(mediaId) {
        if (!this.elements.viewer || !mediaId) return null;
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return null;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
        return activeMedia;
      }

      setActiveMedia(mediaId, prepend) {
        if (!this.elements.viewer || !mediaId) return;

        let activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        const isMobile = this.mobileMql && this.mobileMql.matches;
        activeMedia = this.setActiveMediaState(mediaId) || activeMedia;

        if (prepend && activeMedia.parentElement) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement) activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider && typeof this.elements.viewer.resetPages === 'function') {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        if (isMobile) {
          // Variant image switching on mobile should move the native x-axis
          // gallery only. It must never yank the entire PDP or create a
          // vertical nested-scroll feeling.
          window.requestAnimationFrame(() => {
            const scroller = this.elements.viewer.slider || activeMedia.parentElement;
            if (scroller && typeof scroller.scrollTo === 'function') {
              const gutter = 6;
              scroller.scrollTo({
                left: Math.max(activeMedia.offsetLeft - gutter, 0),
                behavior: 'smooth',
              });
            }
          });
          this.playActiveMedia(activeMedia);
          return;
        }

        window.setTimeout(() => {
          if (this.elements.thumbnails && activeMedia.parentElement) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        const position = activeThumbnail.dataset ? activeThumbnail.dataset.mediaPosition : undefined;
        if (position) this.announceLiveRegion(activeMedia, position);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));

        const button = thumbnail.querySelector('button');
        if (button) button.setAttribute('aria-current', true);

        if (typeof this.elements.thumbnails.isSlideVisible === 'function' && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        if (this.elements.thumbnails.slider) this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion || !activeItem || !position || !window.accessibilityStrings) return;
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (window.pauseAllMedia) window.pauseAllMedia();
        if (!activeItem) return;
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia && typeof deferredMedia.loadContent === 'function') deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer || !this.elements.viewer.slider || !this.elements.viewer.sliderItems) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
